// Data-source table columns — the domain cells rendered inside the common
// DataTable: the glyph-tiled name, the type chip, the monospace external id, the
// absolute last-updated stamp, the updater's avatar, the record count, and the
// row kebab.
//
// Five columns carry `sortable` and the page passes `sort`/`onSort`, so every
// chevron the design draws does something. Their keys ARE the DataSourceSortKey
// members rather than UI-local names, so the header clicked and the value sent
// cannot drift. LAST UPDATED BY is deliberately NOT sortable — the endpoint
// orders by stored fields and that cell is a populated record.
//
// `dataSourceType` is a display string from the wire, so the chip renders it
// literally and there is no i18n key for it.

import { useTranslation } from 'react-i18next';
import { Avatar, Chip, Icon, type DataTableColumn } from '@/components/common';
import { DataSourceSortKey, type DataSourceListItem } from '@/api/endpoints/datasourceApi';
import toneOf from '@/utils/avatarTone';
import initialsOf from '@/utils/initials';
import i18n from '@/i18n/config';
import DataSourceRowActions from './DataSourceRowActions';

const dash = <span className='datasource-table__dash'>—</span>;

// "24 Jun 2026, 09:55". Intl emits its own separator between date and time, so
// the two parts are formatted apart and joined through an i18n key — the same
// idiom as submissionColumns' fmtDateTime.
export const fmtDateTime = (
  iso: string,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const date = new Intl.DateTimeFormat(i18n.language, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
  const time = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
  return t('datasource_datetime', { date, time });
};

interface UseDataSourceColumnsArgs {
  /** Row kebab — each handler receives the row it was opened from. Edit and
   *  Delete are verb-gated by the page: absent = hidden in the kebab. */
  onEdit?: (ds: DataSourceListItem) => void;
  onViewEntries: (ds: DataSourceListItem) => void;
  onDelete?: (ds: DataSourceListItem) => void;
}

export const useDataSourceColumns = ({
  onEdit,
  onViewEntries,
  onDelete,
}: UseDataSourceColumnsArgs): DataTableColumn<DataSourceListItem>[] => {
  const { t } = useTranslation();

  return [
    {
      key: DataSourceSortKey.SchemaName,
      label: t('datasource_col_schema_name'),
      width: 'minmax(14rem, 1.6fr)',
      sortable: true,
      render: (ds) => (
        <span className='datasource-table__name'>
          <span className='datasource-table__tile' aria-hidden='true'>
            <Icon name='database' size={16} />
          </span>
          <span className='datasource-table__name-text'>{ds.schemaName}</span>
        </span>
      ),
    },
    {
      key: DataSourceSortKey.DataSourceType,
      label: t('datasource_col_field_type'),
      width: 'minmax(9rem, 1fr)',
      sortable: true,
      render: (ds) =>
        ds.dataSourceType ? <Chip variant='neutral'>{ds.dataSourceType}</Chip> : dash,
    },
    {
      key: DataSourceSortKey.ExternalId,
      label: t('datasource_col_external_id'),
      width: 'minmax(9rem, 1fr)',
      sortable: true,
      render: (ds) =>
        ds.externalId ? <span className='datasource-table__code'>{ds.externalId}</span> : dash,
    },
    {
      key: DataSourceSortKey.LastUpdatedDate,
      label: t('datasource_col_last_updated'),
      width: 'minmax(10rem, 1fr)',
      sortable: true,
      render: (ds) =>
        ds.lastUpdatedDate ? (
          <span className='datasource-table__muted'>{fmtDateTime(ds.lastUpdatedDate, t)}</span>
        ) : (
          dash
        ),
    },
    {
      key: 'lastUpdatedBy',
      label: t('datasource_col_last_updated_by'),
      width: 'minmax(10rem, 1fr)',
      render: (ds) =>
        ds.lastUpdatedBy ? (
          <span className='datasource-table__person'>
            <Avatar
              initials={initialsOf(ds.lastUpdatedBy.fullName)}
              size={32}
              tone={toneOf(ds.lastUpdatedBy.fullName)}
            />
            <span className='datasource-table__person-name'>{ds.lastUpdatedBy.fullName}</span>
          </span>
        ) : (
          dash
        ),
    },
    {
      key: DataSourceSortKey.TotalRows,
      label: t('datasource_col_records'),
      width: '7rem',
      sortable: true,
      render: (ds) => <span className='datasource-table__records'>{ds.totalRows}</span>,
    },
    {
      key: 'actions',
      label: '',
      width: '3.5rem',
      align: 'end',
      render: (ds) => (
        <DataSourceRowActions
          onEdit={onEdit && (() => onEdit(ds))}
          onViewEntries={() => onViewEntries(ds)}
          onDelete={onDelete && (() => onDelete(ds))}
        />
      ),
    },
  ];
};
